<!--
  @component PhoneInput.svelte
  @deprecated Use PhoneInput.svelte from application/app/frontend/Catalyst/components/PhoneInput/PhoneInput.svelte
  @todo Remove this component in favour of design system component.
-->

<script lang="ts">
  import {getContext, onMount, tick} from 'svelte';
  import type {Plugin} from 'intl-tel-input';
  import 'intl-tel-input/build/js/utils.js';
  import Input from 'Components/Common/Form/Input.svelte';
  import {AsYouType, isValidPhoneNumber} from 'libphonenumber-js/max';
  import type {CountryCode} from 'libphonenumber-js';
  import {formatNational} from 'Utility/formatters/phone';
  import {phoneInput} from 'Utility/phone/intlTelInput';
  import OrientedTooltip from 'Components/InvestorFlow/OrientedTooltip.svelte';
  import {phoneTooltip} from 'Store/InvestorFlowStore';
  import type {GetContextForm} from 'Types/event';

  export let name: string = '';
  export let value: string = '';
  export let label: string = '';
  export let placeholder: string = '';
  export let required: boolean = false;
  export let disabled: boolean = false;
  export let newLayout: boolean = false;
  export let isFocused: boolean = false;
  export let autocomplete: string = 'tel';
  export let containerClass: string = 'form-group';
  export let labelClass: string = '';
  export let readonly: boolean = false;
  export let isValid: boolean = true;
  export let hasTooltip: boolean = false;
  export let initialCountry: string = 'US';

  const DEFAULT_PHONE_TOOLTIP =
    'Please enter a valid phone number. e.g. +16478294546';

  let inputElement: HTMLInputElement = null;
  let hiddenInput: HTMLInputElement = null;

  const {errors, onInput, onBlur, onChange} = getContext(
    'form'
  ) as GetContextForm;

  let iti: Plugin;
  let displayValue: string = '';

  $: visibleInputName = `visible-${name}`;
  $: invalidFormTooltip = hasTooltip && !isValid;

  onMount(() => {
    if (inputElement) {
      iti = phoneInput(inputElement, {
        preferredCountries: ['us', 'ca', 'gb'],
        initialCountry: initialCountry,
      });

      iti.setNumber(value || '');

      if (!value) return;

      // this parses & formats the first phone in a string (ie "4167388376 home" -> (416) 738-8376)
      displayValue = new AsYouType(
        (iti.getSelectedCountryData().iso2?.toUpperCase() ||
          'US') as CountryCode
      ).input(value);
      iti.setNumber(displayValue);

      value = iti.getNumber();

      // force validation to run if mounted with a value that couldn't be parsed into a valid number
      if (!isValidPhoneNumber(value)) {
        tick().then(() => dispatchChange());
      } else {
        displayValue = formatNational(value, {nationalPrefix: false});
      }
    }
  });

  const updateValue = () => {
    displayValue = new AsYouType(
      (iti.getSelectedCountryData().iso2?.toUpperCase() || 'US') as CountryCode
    ).input(inputElement.value);
    iti.setNumber(displayValue); // drops characters after phone #
    value = iti.getNumber(); // update the value of the hidden input field with E.164 format

    // trigger input event on hidden input with E.164 format for validation
    tick().then(() => dispatchChange());
  };

  const onFocusInput = () => {
    if (hasTooltip) {
      phoneTooltip.set(true);
    }
  };

  const onBlurInput = () => {
    updateValue();
    hiddenInput.dispatchEvent(new Event('blur'));
    if (hasTooltip) {
      phoneTooltip.set(false);
    }
  };

  const onKeydown = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      updateValue();
    }
  };

  const dispatchChange = (): void => {
    hiddenInput.dispatchEvent(new Event('change'));
  };
</script>

<div
  class="phone-input"
  class:invalid-form-tooltip={invalidFormTooltip}
  data-testid="phone-input"
>
  <Input
    id={visibleInputName}
    {name}
    type="tel"
    {containerClass}
    {label}
    {labelClass}
    {newLayout}
    {isFocused}
    {hasTooltip}
  >
    <div slot="input" class:is-invalid={$errors?.[name]?.error} class:newLayout>
      <input
        bind:this={hiddenInput}
        id={name}
        {name}
        hidden
        {value}
        on:input={onInput}
        on:blur={onBlur}
        on:change={() => onChange(name, value)}
        data-testid="input-{name}"
      />
      <input
        id={visibleInputName}
        name={visibleInputName}
        bind:this={inputElement}
        value={displayValue}
        class={`form-control ${$$props.class}`}
        class:is-invalid={$errors?.[name]?.error}
        class:is-focused={isFocused}
        class:has-tooltip={hasTooltip}
        {placeholder}
        {required}
        {disabled}
        data-testid="phone-input-{name}"
        {autocomplete}
        {readonly}
        type="tel"
        on:change={updateValue}
        on:focus={onFocusInput}
        on:blur={onBlurInput}
        on:keydown={onKeydown}
      />
    </div>
    <div class="tooltip-wrapper" class:show={hasTooltip} slot="tooltip">
      {#if invalidFormTooltip}
        <OrientedTooltip
          tooltipText={DEFAULT_PHONE_TOOLTIP}
          position="top"
          error={!isValid}
          {isFocused}
          fixedLayout
          inputName={name}
        />
      {/if}
    </div>
  </Input>
</div>

<style lang="postcss">
  .phone-input {
    position: relative;

    & .has-tooltip {
      width: 100%;
    }

    & :global(.iti) {
      width: 100%;
    }
  }

  .tooltip-wrapper {
    position: absolute;
    display: none;
    top: calc(50% - 2rem);
    width: 100%;

    &.show {
      display: block;
    }
  }

  .has-tooltip {
    border: solid 0.1rem #d9d9d9;
    border-radius: 0.25rem;
    height: 2.5rem;
    box-shadow: none;

    &:focus,
    &:active {
      border: solid 0.1rem #d9d9d9;
      box-shadow: none;
      outline: none;
    }
  }

  .invalid-form-tooltip {
    & .has-tooltip {
      border-color: red;
    }
  }

  .newLayout {
    & input {
      box-sizing: border-box;
      height: 2.75rem;
      background: #ffffff;
      border: 0.065rem solid #d0d5dd;
      box-shadow: 0 0.065rem 0.125rem rgba(16, 24, 40, 0.05);
      border-radius: 0.25rem;
    }
  }
</style>
